/* eslint-disable no-unused-expressions */
import React from 'react'
import dotProp from 'dot-prop'
import Input from './input'

interface IAutocompleteProps extends React.HTMLProps<HTMLInputElement> {
  searchFields: string[]
  items: { [key: string]: any }[]
  renderItem: (item: any) => React.ReactNode
  onSelectItem?: (item: any) => void
  value?: string
  icon?: React.ReactNode | string
  block?: boolean
  blurOnSelect?: boolean
  skeleton?: boolean
}

const Autocomplete: React.FC<IAutocompleteProps> = ({
  searchFields,
  items,
  value,
  block,
  blurOnSelect,
  renderItem,
  onSelectItem,
  onChange,
  ...rest
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(Boolean(value?.length))
  const [activeItemIndex, setActiveItemIndex] = React.useState<number>(-1)

  const wrapper = React.useRef<HTMLDivElement>(null)
  const results = React.useRef<HTMLDivElement>(null)
  const result = React.useRef<HTMLDivElement>(null)

  const handleKeyDown = React.useCallback((e: any) => {
    switch (e.keyCode) {
      case 27:
        setIsOpen(false)
        setActiveItemIndex(0)
        break
      case 38:
        setActiveItemIndex((index) => {
          results.current?.scrollTo({
            top: (index - 1) * (result.current?.clientHeight || 0)
          })
          return !index ? 0 : index - 1
        })
        break
      case 40:
        setActiveItemIndex((index) => {
          results.current?.scrollTo({
            top: (index + 1) * (result.current?.clientHeight || 0)
          })
          return index + 1
        })
        break
    }
  }, [])

  const handleClick = React.useCallback((e: any) => {
    if (!wrapper.current || wrapper.current.contains(e.target)) {
      return
    }

    setIsOpen(false)
    setActiveItemIndex(0)
  }, [])

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClick)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('click', handleClick)
    }
  }, [handleKeyDown, handleClick])

  const matchingItems = items.filter((item) => {
    if (!value) return

    const searchableValues = searchFields.map((field) =>
      dotProp.get(item, field)
    )

    return searchableValues.some((v: string) =>
      v.toLowerCase().includes(value.toLowerCase())
    )
  })

  return (
    <div
      ref={wrapper}
      className={`autocomplete-wrapper ${block ? 'block' : ''}`}
      onKeyDown={(e) => {
        if (e.keyCode === 13) {
          e.preventDefault()
          if (onSelectItem) {
            onSelectItem(matchingItems[activeItemIndex])
            setActiveItemIndex(0)
            setIsOpen(false)

            if (blurOnSelect) {
              document.getElementById('autocomplete-input')?.blur()
            }
          }
        }
      }}
    >
      <Input
        {...rest}
        id='autocomplete-input'
        value={value}
        block={block}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          if (onChange) {
            onChange(e)
            setIsOpen(true)
          }
        }}
      />

      {isOpen && value && (
        <div className='autocomplete-results' ref={results}>
          {matchingItems.map((item, i) => (
            <div
              ref={result}
              onClick={() => {
                setIsOpen(false)
                setActiveItemIndex(0)
                if (!blurOnSelect) {
                  document.getElementById('autocomplete-input')?.focus()
                  setIsOpen(false)
                }
                if (onSelectItem) {
                  onSelectItem(item)
                }
              }}
              className={`autocomplete-result ${
                activeItemIndex === i ? 'autocomplete-result-active' : ''
              }`}
              key={Math.random()}
            >
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Autocomplete
